import { isMenuSection } from '@/shared/layouts/menu/types';
import type { MenuDefinition, MenuEntry } from '@/shared/layouts/menu/types';
import type { PermissionContextValue } from '../../shared/providers/PermissionContext';
import type { RbacPrincipalType } from '../../services/rbacSettings';

export const HITOWA_RBAC_SETTINGS_PATH = '/settings/permissions';
export const HITOWA_RBAC_SETTINGS_VIEW_PERMISSION = 'MST_PERMISSION.LIST';
export const HITOWA_RBAC_SETTINGS_EDIT_PERMISSION = 'MST_PERMISSION.EDIT';
export const HITOWA_RBAC_SETTINGS_SCOPE = 'all' as const;

export const assignmentEditPermissions = (
  principalType: RbacPrincipalType
): [string, string] => [
  HITOWA_RBAC_SETTINGS_EDIT_PERMISSION,
  principalType === 'massager' ? 'MST_THERAPIST.EDIT' : 'MST_USER.EDIT',
];

type RbacSettingsAccess = Pick<
  PermissionContextValue,
  'enabled' | 'isBypassed' | 'isReady' | 'isError' | 'principal' | 'canAction' | 'canAccessPath'
>;

/**
 * RBAC Settings is a tenant-wide administration surface. A permission code at
 * a narrower clinic/location/own scope must not reveal any of its entry points.
 */
export const canAccessRbacSettings = (access: RbacSettingsAccess): boolean =>
  access.enabled &&
  !access.isBypassed &&
  access.isReady &&
  !access.isError &&
  Boolean(access.principal) &&
  access.canAction(
    HITOWA_RBAC_SETTINGS_VIEW_PERMISSION,
    'view',
    HITOWA_RBAC_SETTINGS_SCOPE
  ) &&
  access.canAccessPath(HITOWA_RBAC_SETTINGS_PATH, 'view', HITOWA_RBAC_SETTINGS_SCOPE);

const removeSettingsEntry = (entries: MenuEntry[]): MenuEntry[] => {
  const filtered = entries.filter(
    entry => isMenuSection(entry) || entry.path !== HITOWA_RBAC_SETTINGS_PATH
  );

  return filtered.filter((entry, index) => {
    if (!isMenuSection(entry)) return true;
    return filtered.slice(index + 1).some(candidate => !isMenuSection(candidate));
  });
};

/** Keep Sỹ's renderer unchanged; narrow the API menu data before it reaches it. */
export const enforceRbacSettingsMenuAccess = (
  menu: MenuDefinition,
  allowed: boolean
): MenuDefinition => {
  if (allowed) return menu;

  return {
    tabs: menu.tabs
      .map(tab => ({
        ...tab,
        groups: tab.groups
          .filter(group => group.path !== HITOWA_RBAC_SETTINGS_PATH)
          .flatMap(group => {
            if (!group.items) return [group];
            const items = removeSettingsEntry(group.items);
            return items.some(entry => !isMenuSection(entry)) ? [{ ...group, items }] : [];
          }),
      }))
      .filter(tab => tab.groups.length > 0),
  };
};
